import React, { useContext, useEffect, useMemo } from "react";
import ChatContext from "../context";
import { getCredits } from "../send-message";

const SessionUsage = () => {
  const { store, updateData } = useContext(ChatContext);
  const creditsData = store.creditsData ?? null;
  const SESSION_LIMIT = 1; // $1 per session
  const BUDGET_LIMIT = 10; // $10 budget limit for overall usage

  const hasValidCreditsData =
    creditsData &&
    creditsData.status !== "error" &&
    typeof creditsData.tracked_usage?.all_time?.estimated_cost_usd === "number";

  // Only show real figures after the user has sent at least one message and received a response
  const hasReceivedFirstResponse = store.messages?.some(
    (m) => m.senderName === "Zucky"
  );
  const showStats = hasReceivedFirstResponse;

  // Model from credits API (pricing_info.model in the response)
  const modelName =
    creditsData?.pricing_info?.model ??
    creditsData?.model ??
    creditsData?.model_name ??
    null;

  // Fetch on mount if we don't have data yet (backup to App.js load)
  useEffect(() => {
    if (!hasValidCreditsData) {
      const fetchCredits = async () => {
        const data = await getCredits();
        updateData((prev) => ({ ...prev, creditsData: data }));
      };
      fetchCredits();
    }
  }, [hasValidCreditsData, updateData]);

  // Refresh credits every 30s
  useEffect(() => {
    const fetchCredits = async () => {
      const data = await getCredits();
      updateData((prev) => ({ ...prev, creditsData: data }));
    };
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [updateData]);

  // Calculate session usage from messages sent in this session
  // Count unique user messages (excluding regenerated duplicates)
  const { sessionUsage, uniqueUserMessageCount } = useMemo(() => {
    if (!store.name || !store.messages || store.messages.length === 0) {
      return { sessionUsage: 0, uniqueUserMessageCount: 0 };
    }

    const userMessages = store.messages.filter(
      (msg) => msg.senderName === store.name
    );

    if (userMessages.length === 0) {
      return { sessionUsage: 0, uniqueUserMessageCount: 0 };
    }

    // Track unique prompts to avoid counting regenerated messages
    const uniquePrompts = new Set();
    userMessages.forEach((msg) => {
      // Use originalPrompt if available, otherwise use content
      const prompt = msg.originalPrompt || msg.content;
      if (prompt && prompt.trim()) {
        uniquePrompts.add(prompt.trim());
      }
    });

    const uniqueCount = uniquePrompts.size;
    // Estimate: each message costs approximately $0.015
    const estimatedSessionCost = uniqueCount * 0.015;
    
    return {
      sessionUsage: estimatedSessionCost,
      uniqueUserMessageCount: uniqueCount,
    };
  }, [store.messages, store.name]);

  // Get overall usage from API (only use when we have valid data)
  const overallUsage = hasValidCreditsData
    ? creditsData.tracked_usage.all_time.estimated_cost_usd
    : 0;

  // Calculate overall remaining (accounting for session usage)
  // Overall remaining = Budget limit - Overall used - Session used
  const overallRemaining = Math.max(
    0,
    BUDGET_LIMIT - overallUsage - sessionUsage
  );
  const overallRemainingPercentage =
    BUDGET_LIMIT > 0 ? (overallRemaining / BUDGET_LIMIT) * 100 : 0;

  // Calculate session percentages
  const sessionUsedPercentage = (sessionUsage / SESSION_LIMIT) * 100;

  const getRemainingColor = (percentage) => {
    if (percentage <= 20) return "text-red-700";
    if (percentage <= 40) return "text-orange-700";
    return "text-gray-800";
  };

  const getUsedColor = (percentage) => {
    if (percentage >= 80) return "text-red-700";
    if (percentage >= 60) return "text-orange-700";
    return "text-gray-800";
  };

  return (
    <div className="flex text-black p-2 md:p-3 rounded-[25px] opacity-100 bg-[#D5D5D580] backdrop-blur-[27px] flex-shrink-0">
      <div className="w-full">
        {/* Four column layout */}
        <div className="grid grid-cols-4 gap-x-2 gap-y-2">
          {/* Overall Credits Remaining */}
          <div className="flex flex-col">
            <span className="font-plus-jakarta-sans text-xs text-gray-800 mb-1 font-medium text-center">
              Overall Left
            </span>
            <span
              className={`font-plus-jakarta-sans text-sm md:text-base font-bold text-center ${getRemainingColor(
                overallRemainingPercentage
              )}`}
            >
              {!showStats
                ? "–"
                : !hasValidCreditsData
                  ? "…"
                  : overallRemainingPercentage >= 100
                    ? "–"
                    : `${overallRemainingPercentage.toFixed(1)}%`}
            </span>
          </div>

          {/* Session Used */}
          <div className="flex flex-col">
            <span className="font-plus-jakarta-sans text-xs text-gray-800 mb-1 font-medium text-center">
             Session Usage
            </span>
            <span
              className={`font-plus-jakarta-sans text-sm md:text-base font-bold ${getUsedColor(
                sessionUsedPercentage
              )} text-center`}
            >
              {!showStats ? "–" : `${sessionUsedPercentage.toFixed(1)}%`}
            </span>
          </div>

          {/* Message Count */}
          <div className="flex flex-col">
            <span className="font-plus-jakarta-sans text-xs text-gray-800 mb-1 font-medium text-center whitespace-nowrap">
              Unique Prompts
            </span>
            <div className="font-plus-jakarta-sans text-sm md:text-base font-bold text-gray-800 text-center">
              {!showStats ? "–" : uniqueUserMessageCount}
            </div>
          </div>

          {/* Model (from credits API) */}
          <div className="flex flex-col">
            <span className="font-plus-jakarta-sans text-xs text-gray-800 mb-1 font-medium text-center">
              Model
            </span>
            <div className="font-plus-jakarta-sans text-xs md:text-sm font-bold text-gray-800 text-center truncate" title={modelName || undefined}>
              {!showStats ? "–" : (modelName ?? "–")}
            </div>
          </div>
        </div>

        {showStats && sessionUsedPercentage >= 100 && (
          <div className="font-plus-jakarta-sans text-xs text-red-700 font-bold mt-2 p-1.5 bg-red-100 bg-opacity-70 rounded text-center">
            ⚠️ Session limit reached
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionUsage;
